import { motion } from "framer-motion";
import { publishedVentures } from "@/data/site";
import { SectionHead } from "@/components/Blocks";
import { Stagger, itemVariants } from "@/components/motion";
import { StatusBadge, LogoPlate, InterfaceGlyph, VentureLinks } from "@/components/VentureBits";

function Featured({ v }) {
  return (
    <motion.article variants={itemVariants} data-testid={`venture-card-${v.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-si-line bg-si-surface/60 p-6 transition-[border-color,box-shadow] duration-300 hover:border-si-cyan/50 hover:shadow-[0_40px_100px_-40px_rgba(23,182,244,0.5)] sm:p-8 lg:col-span-7 lg:row-span-2">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-[90px]" style={{ background: `${v.accent}33` }} />
      <div className="relative flex flex-wrap items-center justify-between gap-3">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-si-muted">Featured · {v.category}</span>
        <StatusBadge status={v.status} />
      </div>
      <div className="relative mt-6 grid flex-1 gap-4 sm:grid-cols-5">
        <LogoPlate v={v} caption className="aspect-[16/10] sm:col-span-3 sm:aspect-auto sm:min-h-[220px] [clip-path:polygon(0_0,100%_0,100%_86%,88%_100%,0_100%)]" />
        <div className="transition-transform duration-500 group-hover:-translate-y-2 group-hover:scale-[1.02] sm:col-span-2">
          <InterfaceGlyph v={v} />
        </div>
      </div>
      <div className="relative mt-8">
        <h3 className="text-[clamp(2rem,3.6vw,3rem)] font-extrabold leading-none tracking-[-0.03em]">{v.name}</h3>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-si-muted sm:text-lg">{v.shortDescription}</p>
        <div className="mt-6"><VentureLinks v={v} /></div>
      </div>
    </motion.article>
  );
}

function Small({ v, wide }) {
  return (
    <motion.article variants={itemVariants} data-testid={`venture-card-${v.slug}`}
      className={`group relative flex flex-col overflow-hidden rounded-3xl border border-si-line bg-si-surface/60 p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-si-cyan/50 sm:p-7 ${wide ? "lg:col-span-12 lg:flex-row lg:items-center lg:gap-10" : "lg:col-span-5"}`}>
      <div className="pointer-events-none absolute -bottom-20 -right-16 h-52 w-52 rounded-full blur-[80px] transition-opacity duration-300 group-hover:opacity-100 opacity-60" style={{ background: `${v.accent}2a` }} />
      <LogoPlate v={v} className={`relative ${wide ? "h-32 lg:w-72 lg:shrink-0" : "h-32"} transition-transform duration-500 group-hover:scale-[1.02]`} />
      <div className={`relative ${wide ? "mt-6 flex-1 lg:mt-0" : "mt-6"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-si-muted">{v.category}</span>
          <StatusBadge status={v.status} />
        </div>
        <h3 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">{v.name}</h3>
        <p className="mt-3 text-base leading-relaxed text-si-muted">{v.shortDescription}</p>
        <div className="mt-5"><VentureLinks v={v} /></div>
      </div>
    </motion.article>
  );
}

export default function VenturesBento({ withHead = true }) {
  const list = publishedVentures();
  const featured = list.find((v) => v.featured) || list[0];
  const rest = list.filter((v) => v !== featured);
  return (
    <section className="relative py-24 sm:py-32" data-testid="ventures-section">
      <div className="wrap">
        {withHead && (
          <SectionHead eyebrow="Ventures portfolio" title={["Our ideas", "in motion."]}
            intro="Independent products, each built around a specific problem and a clear ambition." />
        )}
        <Stagger className="mt-16 grid gap-5 lg:grid-cols-12" gap={0.12}>
          {featured && <Featured v={featured} />}
          {rest.map((v, i) => <Small key={v.id} v={v} wide={i >= 2} />)}
        </Stagger>
      </div>
    </section>
  );
}
